// Location Service - Uses Capacitor Geolocation on native, browser API on web
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

class LocationService {
  constructor() {
    this.watchId = null;
    this.isNative = Capacitor.isNativePlatform();
  }

  // Check if geolocation is supported
  isSupported() {
    if (this.isNative) {
      return true; // Capacitor plugin handles this
    }
    return 'geolocation' in navigator;
  }

  // Check current permission status
  async checkPermission() {
    if (this.isNative) {
      try {
        const status = await Geolocation.checkPermissions();
        return status.location; // 'granted', 'denied', or 'prompt'
      } catch (error) {
        console.error('Error checking permissions:', error);
        return 'prompt';
      }
    }

    // Web fallback
    if (!this.isSupported()) return 'unsupported';
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state;
    } catch (error) {
      return 'unknown';
    }
  }

  // Request permissions (native only - triggers the iOS/Android permission dialog)
  async requestPermission() {
    if (this.isNative) {
      try {
        const status = await Geolocation.requestPermissions();
        return status.location;
      } catch (error) {
        console.error('Error requesting permissions:', error);
        return 'denied';
      }
    }
    // On web, permission is requested when getCurrentPosition is called
    return 'prompt';
  }

  // Request location permission and get current position
  async getCurrentLocation(options = {}) {
    if (!this.isSupported()) {
      return { success: false, error: 'Geolocation is not supported' };
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000,
    };

    const finalOptions = { ...defaultOptions, ...options };

    // Native (Capacitor)
    if (this.isNative) {
      try {
        // First request permission
        const permStatus = await Geolocation.requestPermissions();

        if (permStatus.location === 'denied') {
          return {
            success: false,
            error: 'Location permission denied. Please enable location access in Settings.',
            code: 1
          };
        }

        const position = await Geolocation.getCurrentPosition(finalOptions);

        return {
          success: true,
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            heading: position.coords.heading,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        };
      } catch (error) {
        console.error('Geolocation error:', error);
        let errorMessage = 'Failed to get location';

        if (error.message?.includes('denied') || error.message?.includes('permission')) {
          errorMessage = 'Location permission denied. Please enable location access in Settings.';
        } else if (error.message?.includes('unavailable')) {
          errorMessage = 'Location information is unavailable. Please check your device settings.';
        } else if (error.message?.includes('timeout')) {
          errorMessage = 'Location request timed out. Please try again.';
        }

        return { success: false, error: errorMessage };
      }
    }

    // Web fallback
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            success: true,
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
          });
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred.';
          }
          resolve({ success: false, error: errorMessage, code: error.code });
        },
        finalOptions
      );
    });
  }

  // Calculate distance between two points (Haversine formula)
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }

  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  // Geocode a city/zip to coordinates using free Nominatim API
  async geocodeAddress(query) {
    try {
      const encodedQuery = encodeURIComponent(query);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=1&countrycodes=us`,
        {
          headers: {
            'User-Agent': 'CardChase App',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        return {
          success: true,
          coords: {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
          },
          displayName: result.display_name,
          type: result.type,
        };
      } else {
        return { success: false, error: 'Location not found' };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      return { success: false, error: 'Failed to geocode address' };
    }
  }

  // Reverse geocode coordinates to get city/state
  async reverseGeocode(latitude, longitude) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
        {
          headers: {
            'User-Agent': 'CardChase App',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Reverse geocoding request failed');
      }

      const data = await response.json();

      if (data && data.address) {
        return {
          success: true,
          city: data.address.city || data.address.town || data.address.village || data.address.county,
          state: data.address.state,
          zip: data.address.postcode,
          country: data.address.country,
          displayName: data.display_name,
        };
      } else {
        return { success: false, error: 'Could not determine location' };
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return { success: false, error: 'Failed to get location details' };
    }
  }
}

export const locationService = new LocationService();
export default locationService;
